import type { SubmissionErrors, TransitionErrors } from '../types/error';
import { SubmissionError, TransitionError } from './error';
import { ENTRYPOINT } from './config';
import { useOidcStore } from 'src/stores/oidc';
import qs from 'qs';

const MIME_TYPE = 'application/ld+json';
const IMPERSONATION_STORAGE_KEY = 'usj.api.impersonation.target';

function readImpersonationTarget(): string | null {
  try {
    const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

interface ApiOptions extends RequestInit {
  headers?: Headers;
  // parse by qs.stringify
  params?: Record<string, string | string[] | number | number[] | boolean>;
  fields?: string[];
  hydraMemberFields?: string[];
  /**
   * If true, do not send `X-Impersonate-User` even if impersonation is active.
   * Useful for debug tools that need both effective and logged-in views.
   */
  skipImpersonation?: boolean;
}

/**
 * A wrapper of the browser fetch API.
 *
 * @param id
 * @param options
 * @returns
 */
export default async function (id: string, options: ApiOptions = {}) {
  if (typeof options.headers === 'undefined') {
    Object.assign(options, { headers: new Headers() });
  }

  if (options.headers.get('Accept') === null) {
    options.headers.set('Accept', MIME_TYPE);
  }

  if (options.fields) {
    options.headers.set(
      'Fields',
      options.fields.map((o) => `"${o}"`).join(', '),
    );
  }

  if (options.hydraMemberFields) {
    options.headers.set(
      'Fields',
      [
        '"/hydra:member"',
        ...options.hydraMemberFields.map((o) => `"/hydra:member/*/${o}"`),
        '"/hydra:totalItems"',
      ].join(', '),
    );
  }

  const oidcStore = useOidcStore();
  const impersonationTarget = readImpersonationTarget();

  if (oidcStore.access_token) {
    options.headers.set('Authorization', `Bearer ${oidcStore.access_token}`);
  }

  if (!options.skipImpersonation && impersonationTarget) {
    options.headers.set('X-Impersonate-User', impersonationTarget);
  }

  if (
    options.body !== undefined &&
    !(options.body instanceof FormData) &&
    options.headers.get('Content-Type') === null
  ) {
    options.headers.set('Content-Type', MIME_TYPE);
  }

  if (options.params) {
    id = `${id}?` + qs.stringify(options.params, { arrayFormat: 'brackets' });
  }

  // enable CORS for all requests
  Object.assign(options, {
    mode: 'cors',
    // credentials: 'include', // when credentials needed
  });

  let entrypoint = ENTRYPOINT;

  const isMSW = import.meta.env.VITE_MSW;

  if (isMSW) {
    entrypoint = window.location.origin + '/api/v2/';
  }

  const url = new URL(id, entrypoint);
  const entrypointHost = new URL(entrypoint as string).host;

  if (url.host !== entrypointHost) {
    throw new Error(
      `Cross-origin request blocked: "${url.host}" does not match API entrypoint "${entrypointHost}"`,
    );
  }

  const response = await fetch(url, options);

  if (response.status === 401) {
    const canSilentRenew =
      typeof oidcStore.userManager?.signinSilent === 'function';

    if (canSilentRenew) {
      try {
        const renewedUser = await oidcStore.userManager.signinSilent();
        const renewedAccessToken =
          renewedUser?.access_token || oidcStore.access_token;

        if (renewedAccessToken) {
          options.headers.set('Authorization', `Bearer ${renewedAccessToken}`);
        }

        const retryResponse = await fetch(url, options);

        if (retryResponse.ok) {
          return retryResponse;
        }

        if (retryResponse.status === 401) {
          await oidcStore.userManager.signinRedirect();
        }

        return retryResponse;
      } catch {
        await oidcStore.userManager.signinRedirect();
      }
    }
  }

  if (!response.ok) {
    const data = await response.json();
    const error = data['hydra:description'] || response.statusText;

    if (data.violations && data.violations.length > 0) {
      const errors: SubmissionErrors = { _error: error };
      data.violations.forEach(
        (violation: { propertyPath: string; message: string }) => {
          errors[violation.propertyPath] = violation.message;
        },
      );

      throw new SubmissionError(errors);
    }

    if (data.blockers && data.blockers.length > 0) {
      const errors: TransitionErrors = {
        _error: error,
      };
      data.blockers.forEach((blocker: string, index: number) => {
        errors[`blocker-${index}`] = blocker;
      });

      throw new TransitionError(errors);
    }

    throw Error(error);
  }

  return response;
}
